import { SimpleUserAvatar } from '@/components/UserAvatar'
import SimpleUsername from '@/components/SimpleUsername'
import { TInstalledSlashCommand } from '@/atoms/slash-commands'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { cn } from '@/lib/utils'

export interface SlashCommandListProps {
  items: { slashCommand: TInstalledSlashCommand }[]
  command: (payload: { slashCommand: TInstalledSlashCommand }) => void
}

export interface SlashCommandListHandle {
  onKeyDown: (args: SuggestionKeyDownProps) => boolean
}

const SlashCommandList = forwardRef<SlashCommandListHandle, SlashCommandListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectItem = (index: number) => {
    const item = props.items[index]
    if (item) props.command({ slashCommand: item.slashCommand })
  }

  const upHandler = () =>
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
  const downHandler = () => setSelectedIndex((selectedIndex + 1) % props.items.length)
  const enterHandler = () => selectItem(selectedIndex)

  useEffect(() => {
    setSelectedIndex(props.items.length ? 0 : -1)
  }, [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      // nothing to pick: let keys (e.g. Enter for a new line) reach the editor
      if (!props.items.length) return false
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }
      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }
      if (event.key === 'Enter' && selectedIndex >= 0) {
        enterHandler()
        return true
      }
      return false
    }
  }))

  if (!props.items.length)
    return (
      <div className="border rounded-lg bg-background z-50 pointer-events-auto shadow-md px-3 py-2 text-sm text-muted-foreground">
        No slash commands available
      </div>
    )

  return (
    <div className="border rounded-lg bg-background z-50 pointer-events-auto flex flex-col max-h-80 overflow-y-auto shadow-md">
      {props.items.map((item, index) => (
        <button
          className={cn(
            'cursor-pointer text-start items-center m-1 p-2 outline-none transition-colors rounded-md',
            selectedIndex === index && 'bg-accent text-accent-foreground'
          )}
          key={`${item.slashCommand.pubkey}:${item.slashCommand.d}`}
          onClick={() => selectItem(index)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="flex gap-2 w-80 items-center truncate pointer-events-none">
            <SimpleUserAvatar user={item.slashCommand.pubkey} />
            <div className="flex-1 w-0 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold truncate">
                  /{item.slashCommand.command}
                </span>
                <SimpleUsername
                  user={item.slashCommand.pubkey}
                  className="truncate text-muted-foreground text-xs"
                />
              </div>
              {item.slashCommand.title && (
                <div className="text-xs text-muted-foreground truncate">
                  {item.slashCommand.title}
                </div>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  )
})

SlashCommandList.displayName = 'SlashCommandList'

export default SlashCommandList
